import React from 'react';
import { X, Smartphone, Download, CheckCircle2, ShieldCheck } from 'lucide-react';
import { usePWAInstall } from '../utils/pwaInstall';

interface StaffInstallGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const StaffInstallGuideModal: React.FC<StaffInstallGuideModalProps> = ({
  isOpen,
  onClose
}) => {
  const { isStaffPromptReady, platform, triggerInstall } = usePWAInstall();
  const { isIOS, isSamsung } = platform;

  React.useEffect(() => {
    if (isOpen) {
      localStorage.setItem('blishka_installed_portal', 'staff');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLaunchDirectStaffUrl = () => {
    try {
      localStorage.setItem('blishka_installed_portal', 'staff');
    } catch {}
    window.location.href = '/staff?source=pwa&app=staff&install=true';
  };

  const handleNativeInstall = async () => {
    if (isStaffPromptReady) {
      const outcome = await triggerInstall('staff');
      if (outcome === 'accepted') {
        onClose();
      } else if (outcome === 'portal_mismatch') {
        handleLaunchDirectStaffUrl();
      }
    } else {
      handleLaunchDirectStaffUrl();
    }
  };

  return (
    <div className="pwa-guide-backdrop" onClick={onClose} style={{ zIndex: 11000 }}>
      <div className="pwa-guide-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
        {/* Header */}
        <div className="pwa-guide-header">
          <div
            className="pwa-guide-icon-badge"
            style={{
              background: 'linear-gradient(135deg, rgba(125, 26, 56, 0.12) 0%, rgba(125, 26, 56, 0.22) 100%)',
              color: 'var(--blishka-berry)'
            }}
          >
            <img src="/brand-logo.png" alt="Blishka Staff App" className="pwa-modal-brand-img" />
          </div>
          <div className="pwa-guide-title-box">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <h4 className="pwa-guide-title" style={{ margin: 0, fontSize: '1.05rem' }}>
                Blishka Staff App
              </h4>
              <span
                style={{
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  background: 'rgba(125, 26, 56, 0.1)',
                  color: 'var(--blishka-berry)',
                  padding: '0.12rem 0.45rem',
                  borderRadius: '999px'
                }}
              >
                Duty Portal
              </span>
            </div>
            <p className="pwa-guide-sub" style={{ margin: '0.15rem 0 0', fontSize: '0.78rem' }}>
              Download to your phone for 1-tap duty access
            </p>
          </div>
          <button
            type="button"
            className="pwa-guide-close"
            onClick={onClose}
            aria-label="Close guide"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="pwa-guide-body" style={{ padding: '1.25rem 1.25rem 1rem' }}>
          {/* Quick Action Button for Android / Chrome if prompt is available */}
          {isStaffPromptReady && !isIOS && (
            <div
              style={{
                background: 'linear-gradient(135deg, #fdf8f9 0%, #faeef1 100%)',
                border: '1.5px solid rgba(125, 26, 56, 0.25)',
                borderRadius: '14px',
                padding: '1rem',
                marginBottom: '1.25rem',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--blishka-berry)', marginBottom: '0.4rem' }}>
                ⚡ 1-Tap Direct Installation Ready
              </div>
              <p style={{ fontSize: '0.78rem', color: '#665357', margin: '0 0 0.85rem', lineHeight: 1.4 }}>
                Aapke phone par Blishka Staff App seedha home screen par add ho jayega.
              </p>
              <button
                type="button"
                onClick={handleNativeInstall}
                className="btn btn-berry"
                style={{
                  width: '100%',
                  padding: '0.65rem',
                  fontSize: '0.9rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  borderRadius: '10px'
                }}
              >
                <Download size={16} />
                <span>Install Blishka Staff App Now</span>
              </button>
            </div>
          )}

          {!isStaffPromptReady && !isIOS && (
            <div
              style={{
                background: 'linear-gradient(135deg, #fdf8f9 0%, #faeef1 100%)',
                border: '1.5px solid rgba(125, 26, 56, 0.25)',
                borderRadius: '14px',
                padding: '1rem',
                marginBottom: '1.25rem',
                textAlign: 'center'
              }}
            >
              <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--blishka-berry)', marginBottom: '0.4rem' }}>
                ⚡ Install Dedicated Staff App
              </div>
              <p style={{ fontSize: '0.78rem', color: '#665357', margin: '0 0 0.85rem', lineHeight: 1.4 }}>
                Staff App install karne ke liye direct staff link kholein:
              </p>
              <button
                type="button"
                onClick={handleLaunchDirectStaffUrl}
                className="btn btn-berry"
                style={{
                  width: '100%',
                  padding: '0.65rem',
                  fontSize: '0.9rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  borderRadius: '10px'
                }}
              >
                <span>🔗 Open Direct Staff URL to Install</span>
              </button>
            </div>
          )}

          {/* Guide Steps */}
          {isIOS ? (
            <div className="pwa-android-help-content">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  color: 'var(--blishka-berry)',
                  fontWeight: 700,
                  fontSize: '0.86rem',
                  marginBottom: '0.5rem'
                }}
              >
                <Smartphone size={16} />
                <span>iPhone / iPad Installation Steps:</span>
              </div>
              <ol className="pwa-steps-list" style={{ marginTop: '0.5rem' }}>
                <li>
                  Check karein ki Safari me <strong>staff</strong> URL open hai. Agar zaroorat ho to tap karein:
                  <div style={{ marginTop: '0.4rem', marginBottom: '0.4rem' }}>
                    <a
                      href="/staff?source=pwa&app=staff"
                      className="btn btn-sm"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.4rem',
                        fontSize: '0.78rem',
                        textDecoration: 'none',
                        background: 'rgba(125, 26, 56, 0.08)',
                        color: 'var(--blishka-berry)',
                        border: '1px solid rgba(125, 26, 56, 0.25)',
                        fontWeight: 700,
                        padding: '0.35rem 0.75rem',
                        borderRadius: '8px'
                      }}
                    >
                      <span>🔗 Open Direct Staff Safari URL</span>
                    </a>
                  </div>
                </li>
                <li>
                  Safari browser ke neeche <strong>Share button (⎋)</strong> dabayein.
                </li>
                <li>
                  Title check karein (<strong>Blishka Staff</strong>) aur <strong>&quot;Add to Home Screen&quot;</strong> (+) chunein.
                </li>
                <li>
                  Top-right corner me <strong>&quot;Add&quot;</strong> par tap karein.
                </li>
              </ol>
              <div
                style={{
                  background: 'rgba(46, 125, 50, 0.08)',
                  border: '1px solid rgba(46, 125, 50, 0.2)',
                  borderRadius: '10px',
                  padding: '0.65rem 0.85rem',
                  marginTop: '0.85rem',
                  fontSize: '0.78rem',
                  color: '#2e7d32',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.45rem'
                }}
              >
                <CheckCircle2 size={16} style={{ flexShrink: 0 }} />
                <span>Phone screen par &quot;Blishka Staff&quot; app ban jayega!</span>
              </div>
            </div>
          ) : isSamsung ? (
            <div className="pwa-android-help-content">
              <div className="pwa-highlight-box" style={{ background: 'rgba(125, 26, 56, 0.05)', borderColor: 'rgba(125, 26, 56, 0.2)' }}>
                <p className="pwa-highlight-title" style={{ color: 'var(--blishka-berry)' }}>
                  ⭐ Samsung Internet: Zero Alert Method
                </p>
                <ol className="pwa-steps-list" style={{ marginTop: '0.4rem' }}>
                  <li>
                    Samsung browser ke bottom-right me <strong>Menu (☰)</strong> dabayein.
                  </li>
                  <li>
                    <strong>&quot;+ Add page to&quot;</strong> par tap karein.
                  </li>
                  <li>
                    <strong>&quot;Home screen&quot;</strong> chunein $\rightarrow$ Tap <strong>&quot;Add&quot;</strong>.
                  </li>
                </ol>
              </div>
              <p className="pwa-alt-note" style={{ marginTop: '0.75rem' }}>
                👉 <em>Tip:</em> Google Chrome me kholne par 1-tap me bina kisi alert ke install ho jata hai.
              </p>
            </div>
          ) : (
            <div className="pwa-android-help-content">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  color: 'var(--blishka-berry)',
                  fontWeight: 700,
                  fontSize: '0.86rem',
                  marginBottom: '0.5rem'
                }}
              >
                <Smartphone size={16} />
                <span>Android Chrome Installation:</span>
              </div>
              <ol className="pwa-steps-list" style={{ marginTop: '0.5rem' }}>
                <li>
                  Agar upar &quot;Install Now&quot; button dikh raha hai to uspar tap karein.
                </li>
                <li>
                  Ya Chrome ke upar-right me <strong>Menu (3 dots ⋮)</strong> dabayein.
                </li>
                <li>
                  <strong>&quot;Install app&quot;</strong> ya <strong>&quot;Add to Home screen&quot;</strong> chunein.
                </li>
                <li>
                  Screen par <strong>&quot;Blishka Staff&quot;</strong> app download ho jayegi!
                </li>
              </ol>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.74rem',
                  color: '#6e5d61',
                  marginTop: '0.75rem'
                }}
              >
                <ShieldCheck size={14} style={{ color: '#2e7d32' }} />
                <span>100% Safe · No Play Store account needed · Direct PWA</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pwa-guide-footer" style={{ padding: '0.85rem 1.25rem' }}>
          <button
            type="button"
            className="pwa-guide-btn-primary"
            onClick={onClose}
            style={{ width: '100%', background: 'var(--blishka-berry)' }}
          >
            Samajh Gaya / Got It!
          </button>
        </div>
      </div>
    </div>
  );
};
